import { FC, useEffect, useState } from 'react';
import { Space, Typography } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import Notification from 'components/ui/base/notification/Notification';
import useDeclarations from 'hooks/useDeclarations';
import useRequest from 'hooks/useRequest';
import { getIcs2EnsInvalidationEligibility, invalidateIcs2EnsDeclaration } from 'store/declarations/client';
import { Declaration } from '../../../../store/declarations/declaration';
import { IrelandH7ImportDeclaration } from '../../../../store/declarations/ireland/h7-import-declaration';
import { IrelandImportDeclaration } from '../../../../store/declarations/ireland/import-declaration';
import { canDeclarationBeInvalidated, canEnsBeInvalidated, getDeclarationPayload } from '../../utils/declaration-utils';
import useFormUtils from '../../../../hooks/useFormUtils';
import InvalidateForm from './InvalidateForm';
import InvalidateFormNcts from './InvalidateFormNcts';
import { StyledButton } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import { FooterButton } from 'views/declarations/declaration-tabs/view-only/FormViewOnly';

interface Props {
    visible: boolean;
    onCancel: () => void;
}

/**
 * Reasons the backend gives for refusing an invalidation. Anything unmapped is shown as-is, so a newly
 * added backend reason surfaces as a code rather than silently disappearing from the dialog.
 */
const ics2BlockingReasonMessages: Record<string, string> = {
    ICS2_FILING_REQUIRED: 'This declaration is not an ICS2 filing.',
    REGISTERED_MRN_REQUIRED: 'This filing has not been registered with customs yet.',
    FILING_STATE_PREVENTS_INVALIDATION: 'The current status of this filing does not allow invalidation.',
    CUSTOMS_CONTROL_NOTIFIED: 'Customs has notified a control for this filing, so it can no longer be invalidated.',
    INVALIDATION_IN_PROGRESS: 'An invalidation request for this filing is already in progress.',
    AMENDMENT_IN_PROGRESS: 'An amendment is in progress. Wait for it to complete before invalidating.',
};

const describeBlockingReason = (reason: string) => ics2BlockingReasonMessages[reason] ?? reason;

/**
 * The submit races the eligibility check, so a filing can become ineligible in between. The backend
 * answers 409 with the blocking reasons joined into the error message; pull them back out so the
 * dialog can explain the refusal instead of showing a generic failure toast.
 */
const parseConflictBlockingReasons = (error: any): string[] => {
    if (error?.response?.status !== 409) return [];

    const message: unknown = error.response.data?.message ?? error.response.data?.error ?? error.response.data;
    if (typeof message !== 'string' || !message.trim()) return [];

    const codes = message
        .split(',')
        .map((part) => part.trim())
        .filter((part) => /^[A-Z][A-Z0-9_]*$/.test(part));

    return codes.length ? codes : [message.trim()];
};

const InvalidateModalButton = ({ declaration, newUi }: { declaration: Declaration | undefined; newUi?: boolean }) => {
    const [invalidateModalOpen, setInvalidateModalOpen] = useState<boolean>(false);

    // Same detection the modal uses, so the button and the branch it opens can never disagree.
    const isIcs2Declaration = Boolean(declaration?.ics2ENSDeclaration);

    const showInvalidateButton = isIcs2Declaration
        ? canEnsBeInvalidated(declaration)
        : declaration &&
          ((getDeclarationPayload(declaration) as IrelandImportDeclaration | IrelandH7ImportDeclaration)
              ?.revenueInvalidationRequested ||
              canDeclarationBeInvalidated(declaration?.status)) &&
          declaration?.declarationExternalEntity !== 'CDS';

    return (
        <>
            {showInvalidateButton &&
                (newUi ? (
                    <StyledButton onClick={() => setInvalidateModalOpen(true)}>Invalidate</StyledButton>
                ) : (
                    <FooterButton onClick={() => setInvalidateModalOpen(true)}>Invalidate</FooterButton>
                ))}
            <InvalidateModal visible={invalidateModalOpen} onCancel={() => setInvalidateModalOpen(false)} />
        </>
    );
};

export default InvalidateModalButton;

const InvalidateModal: FC<Props> = ({ visible, onCancel }) => {
    const { isNcts } = useFormUtils();
    const { declaration, getDeclaration } = useDeclarations();
    const { t } = useTranslation('customs_declarations');

    const isICS2 = Boolean(declaration?.ics2ENSDeclaration);

    const [isInvalidatingIcs2, setIsInvalidatingIcs2] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState<string>();
    const [submitBlockingReasons, setSubmitBlockingReasons] = useState<string[]>([]);

    const {
        doRequest: fetchEligibility,
        data: eligibility,
        isLoading: isCheckingEligibility,
    } = useRequest(getIcs2EnsInvalidationEligibility);

    useEffect(() => {
        if (!visible || !isICS2 || !declaration?.id) return;

        setIdempotencyKey(uuidv4());
        setSubmitBlockingReasons([]);
        fetchEligibility(declaration.id);
    }, [visible, isICS2, declaration?.id, fetchEligibility]);

    const title = isICS2 ? 'Invalidate ENS' : 'Invalidate declaration';
    const message = isICS2
        ? 'Are you sure you want to invalidate this ENS?'
        : 'Are you sure you want to invalidate this declaration?';

    const formComponent = isICS2 ? null : isNcts ? (
        <InvalidateFormNcts onCancel={onCancel} />
    ) : (
        <InvalidateForm onCancel={onCancel} />
    );

    const eligibilityReasons = eligibility?.allowed === false ? eligibility.blockingReasons : [];
    // Reasons the submit itself came back with win: the filing may have moved on since the check.
    const blockingReasons = submitBlockingReasons.length ? submitBlockingReasons : eligibilityReasons;
    const isBlocked = blockingReasons.length > 0;

    const handleInvalidateIcs2 = async () => {
        if (!declaration?.id || !idempotencyKey) return;

        setIsInvalidatingIcs2(true);
        try {
            await invalidateIcs2EnsDeclaration(declaration.id, { requestId: idempotencyKey });
            onCancel();
            // Customs answers asynchronously, so the filing is not invalidated yet - only requested.
            Notification({
                type: 'success',
                messageTitle: t('invalidate.ens-submitted-title'),
                description: t('invalidate.ens-submitted-message'),
                placement: 'top',
            });
            getDeclaration(declaration.id);
        } catch (error: any) {
            const reasons = parseConflictBlockingReasons(error);
            if (reasons.length) {
                setSubmitBlockingReasons(reasons);
            } else {
                Notification({
                    type: 'error',
                    messageTitle: t('invalidate.ens-error-title'),
                    description: t('invalidate.ens-error-message'),
                    placement: 'top',
                });
            }
        } finally {
            setIsInvalidatingIcs2(false);
        }
    };

    return (
        <ConfirmationModal
            open={visible}
            width={700}
            type="error"
            destroyOnClose
            icon={<StopOutlined style={{ fontSize: 48 }} />}
            title={title}
            confirmButtonText="Invalidate"
            confirmationButtonLoading={isInvalidatingIcs2}
            confirmationButtonDisabled={isICS2 && (isCheckingEligibility || isBlocked)}
            confirmButtonProps={isICS2 ? undefined : { htmlType: 'submit', form: 'invalidateForm' }}
            onConfirm={isICS2 ? handleInvalidateIcs2 : undefined}
            onCancel={onCancel}
            onClose={onCancel}
            content={
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Typography.Text>{isBlocked ? 'This ENS cannot be invalidated:' : message}</Typography.Text>
                    {isBlocked && (
                        <ul style={{ margin: 0, paddingInlineStart: '2rem' }}>
                            {blockingReasons.map((reason) => (
                                <li key={reason}>
                                    <Typography.Text>{describeBlockingReason(reason)}</Typography.Text>
                                </li>
                            ))}
                        </ul>
                    )}
                    {formComponent}
                </Space>
            }
        />
    );
};
